// Import SCSS as a string - this will be handled by esbuild
import styles from "./styles.scss";
import attentionBarStyles from "./attention-bar.scss";

export default function createWidget() {
  /**
   * Print Widget Integration with Shadow DOM
   *
   * This script handles the integration of a print widget iframe into a webpage.
   *
   * Events sent to iframe:
   * - widget_loaded: Sent when the widget is first loaded
   *   payload: current URL
   *
   * - widget_opened: Sent when the widget is opened
   *   payload: current URL
   *
   * - widget_closed: Sent when the widget is closed
   *   payload: current URL
   *
   * - navigate: Sent when navigation is requested (typically via URL hash)
   *   payload: navigation path
   */
  // Check if running in a staging environment
  const isStaging = window.location.hostname.includes("stg");
  const isLocal = window.location.hostname.includes("stg");

  // Check for Shadow DOM support
  const supportsShadowDOM = !!HTMLElement.prototype.attachShadow;
  if (!supportsShadowDOM) {
    console.log(
      "Shadow DOM not supported in this browser. Widget will not render."
    );
    return;
  }

  const openHashes = ["#print-popup", "#pp"];
  if (window.printWidgetLoaded) {
    console.log("Print widget script already loaded");
    return;
  }
  window.printWidgetLoaded = true;

  const scriptSrc = document.currentScript.src;
  const scriptUrl = new URL(scriptSrc);
  let id = scriptUrl.searchParams.get("id");
  let $btn;
  let shadowRoot; // Store reference to widget shadow root
  let attentionBarShadowRoot; // Store reference to attention bar shadow root
  const mode = scriptUrl.searchParams.get("mode");

  let baseUrl = "https://popup.print.com";

  if (scriptUrl?.href && scriptUrl.href.includes("localhost")) {
    baseUrl = "http://localhost:3200";
  }

  if (scriptUrl?.href && scriptUrl.href.includes("stg")) {
    baseUrl = "https://popup.stg.print.com";
  }

  if (mode == "development") {
    baseUrl = "http://localhost:3200";
  }

  const isPrintPreview = window.location.pathname.includes("widget-preview");

  // Add check for ID in div element if not found in URL params
  if (!id) {
    const targetDiv = document.getElementById("print-widget-target");
    if (targetDiv) {
      id = targetDiv.getAttribute("data-print-id");
    }
  }

  if (!id) {
    window.alert(
      "Please update your print embed code! And clear your site cache!"
    );
  }

  let iframeUrl = `${baseUrl}/widget/${id}`;

  function createPathMatcher(excludedTerms) {
    try {
      return function matchPath(path) {
        try {
          if (typeof path !== "string") {
            throw new Error("Path must be a string");
          }

          // Check if path contains any excluded terms
          return !excludedTerms.some((term) =>
            path.toLowerCase().includes(term)
          );
        } catch (error) {
          console.error("Error in matchPath:", error);
          return true;
        }
      };
    } catch (error) {
      console.error("Error creating matcher:", error);
      return function () {
        return true;
      };
    }
  }

  // Helper function to check if a campaign is currently active
  function isCampaignActive(campaign) {
    // Disable on production feature_fla
    if (!(isStaging || isLocal)) {
      console.log("campaigns are disabled in production");
      return false;
    }

    if (!campaign || !campaign.active) {
      return false;
    }

    const now = new Date();
    const startDate = campaign.start_date
      ? new Date(campaign.start_date)
      : null;
    const endDate = campaign.end_date ? new Date(campaign.end_date) : null;

    // Check if current time is within campaign date range
    if (startDate && now < startDate) {
      return false;
    }
    if (endDate && now > endDate) {
      return false;
    }

    return true;
  }

  // Helper function to filter and process active campaigns
  function processActiveCampaigns(campaigns) {
    if (!Array.isArray(campaigns)) {
      return [];
    }

    return campaigns.filter(isCampaignActive);
  }

  // Add state flag for first load
  let state = {
    isOpen: false,
    isLoaded: false,
    data: {},
    pendingOpen: false,
    pendingNavigation: null,
    firstLoadHandled: false, // Add this new flag
  };

  // Helper function to check if bubbles should be shown
  function shouldShowBubbles(data) {
    // First check if bubbles are enabled in the design
    // console.log("Checking bubbles:", {
    //   bubblesEnabled: data?.design?.bubbles?.enabled,
    //   data: data,
    //   firstLoadHandled: state.firstLoadHandled,
    // });

    if (!data?.design?.bubbles?.enabled) {
      // Bubbles are disabled in design
      return false;
    }

    // For the isPrintPreview always show the bubbles
    if (isPrintPreview) {
      return true;
    }

    // Don't show bubbles if widget is minimized
    if (shouldBeMinimized()) {
      return false;
    }

    return true;
  }

  // Helper function to check if widget should be minimized
  function shouldBeMinimized() {
    try {
      const storageKey = "print_widget_minimized_timestamp";
      const currentTime = new Date().getTime();
      const minimizedTime = localStorage.getItem(storageKey);

      if (!minimizedTime) {
        return false;
      }

      const fiveMinutesInMs = 5 * 60 * 1000;
      const timeDifference = currentTime - parseInt(minimizedTime);

      return timeDifference <= fiveMinutesInMs;
    } catch (error) {
      console.error("Error checking minimization state:", error);
      return false;
    }
  }

  function intPrintInterface(widgetKey) {
    const printWidget = document.createElement("div");
    printWidget.setAttribute("id", "print-widget-container");

    // Create widget shadow root
    shadowRoot = printWidget.attachShadow({ mode: "open" });

    // Add styles to widget shadow DOM
    const style = document.createElement("style");
    style.textContent = styles;
    shadowRoot.appendChild(style);

    // Add content to widget shadow DOM
    const widgetContent = document.createElement("div");
    widgetContent.id = "print-widget";
    widgetContent.innerHTML = `
      <iframe id="print-frame-widget" src="${iframeUrl}"></iframe>
      <div id="btn-trigger-widget"></div>
    `;
    shadowRoot.appendChild(widgetContent);

    document.body.appendChild(printWidget);

    // Add position relative to body for shadow DOM positioning context
    document.body.style.position = document.body.style.position || "relative";

    // Create attention bar shadow DOM container
    const attentionBarContainer = document.createElement("div");
    attentionBarContainer.setAttribute("id", "print-attention-bars-container");

    // Style the container to stretch full page height
    attentionBarContainer.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 999999997;
    `;

    // Create attention bar shadow root
    attentionBarShadowRoot = attentionBarContainer.attachShadow({
      mode: "open",
    });

    // Add attention bar styles to shadow DOM
    const attentionBarStyle = document.createElement("style");
    attentionBarStyle.textContent = attentionBarStyles;
    attentionBarShadowRoot.appendChild(attentionBarStyle);

    // Add attention bar content container to shadow DOM
    const attentionBarsContent = document.createElement("div");
    attentionBarsContent.className = "attention-bars-container";
    attentionBarShadowRoot.appendChild(attentionBarsContent);

    document.body.appendChild(attentionBarContainer);

    // Store shadowRoot references in the global object
    window.printWidget.shadowRoot = shadowRoot;
    window.printWidget.attentionBarShadowRoot = attentionBarShadowRoot;

    const frameWidget = shadowRoot.getElementById("print-frame-widget");
    const btn = shadowRoot.getElementById("btn-trigger-widget");
    $btn = btn;
    if (!frameWidget || !btn) {
      console.error("Required elements not found");
      return;
    }

    let matcher = () => true;
    const components = {
      html_open: `
        <div class="trigger-open">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-7 h-7"><line x1="18" x2="6" y1="6" y2="18"></line><line x1="6" x2="18" y1="6" y2="18"></line></svg>
        </div>`,
      html_closed: (data = {}) => `
        <div class="trigger-closed-wrapper">
          <div class="trigger-closed rocking">
            ${data?.design?.icon ? '<img src="' + data?.design?.icon + '" class="launcher-icon"/>' : ""}
            <span class="trigger-text">
              ${data?.design?.launcher_text || "Open"}
            </span>
          </div>
          ${!shouldShowBubbles(data) ? renderCloseIcon() : ""}
        </div>`,
    };

    // Add new click event listener for close icon
    shadowRoot.addEventListener("click", (e) => {
      // Minimize launcher
      if (e.target.closest("#trigger-close-icon")) {
        e.stopPropagation();
        document.body.classList.add("popup-store-launcher-minimized");
        widgetContent.classList.add("popup-store-launcher-minimized");
        removeBubbles();
        return;
      }

      // Toggle widget
      if (e.target.closest("#btn-trigger-widget")) {
        e.stopPropagation();
        toggleOpen();
        return;
      }
    });

    window.addEventListener(
      "message",
      function (e) {
        try {
          if (typeof e.data === "string") {
            const { event, data } = JSON.parse(e.data);

            if (event === "close") {
              toggleOpen();
            }

            if (event === "loaded-popup-store" && !state.isLoaded) {
              if (data) {
                state.data = data;

                // Process campaigns if they exist
                if (data.campaigns) {
                  const activeCampaigns = processActiveCampaigns(
                    data.campaigns
                  );

                  // Add campaign CTAs to existing bubble system
                  if (!data.design) {
                    data.design = {};
                  }
                  if (!data.design.bubbles) {
                    data.design.bubbles = {};
                  }
                  if (!data.design.bubbles.ctas) {
                    data.design.bubbles.ctas = {};
                  }

                  // Add campaign CTAs to bubbles
                  activeCampaigns.forEach((campaign) => {
                    if (campaign.cta_bubble_settings?.enabled) {
                      const settings = campaign.cta_bubble_settings;
                      data.design.bubbles.ctas[campaign.id] = {
                        url: `#pp/campaigns/${campaign.slug}`,
                        icon: {
                          url: "",
                          enabled: false,
                        },
                        text: settings.title || "Check The Campaign",
                        subtext: settings.subtitle || "",
                        order: Object.keys(data.design.bubbles.ctas).length,
                        enabled: true,
                        color: settings.color || "#f93737",
                        text_color: settings.text_color || "#fafafa",
                      };
                    }
                  });

                  // Render attention bars for active campaigns
                  renderAttentionBars(activeCampaigns);
                }
              }
              btn.innerHTML = components.html_closed(state.data || {});
              frameWidget.style.display = "none";

              // First check if widget should be minimized based on previous visit
              const isMinimized = shouldBeMinimized();
              if (isMinimized) {
                document.body.classList.add("popup-store-launcher-minimized");
                widgetContent.classList.add("popup-store-launcher-minimized");
              }

              // Then add body class based on bubbles state and visibility
              // Note: shouldShowBubbles will return false if widget is minimized
              const shouldShowBubblesNow = shouldShowBubbles(data);
              if (shouldShowBubblesNow) {
                document.body.classList.add("bubbles-enabled");
                widgetContent.classList.add("bubbles-enabled");
                document.body.classList.remove("bubbles-disabled");
                widgetContent.classList.remove("bubbles-disabled");
              } else {
                document.body.classList.add("bubbles-disabled");
                widgetContent.classList.add("bubbles-disabled");
                document.body.classList.remove("bubbles-enabled");
                widgetContent.classList.remove("bubbles-enabled");
              }

              // Check if current domain is allowed

              const domainCheckIsEnabled = false;
              if (domainCheckIsEnabled) {
                if (data?.domains?.length > 0) {
                  const currentDomain = window.location.hostname;
                  const isAllowed = data.domains.some(
                    (domain) =>
                      currentDomain === domain ||
                      currentDomain.endsWith(`.${domain}`)
                  );

                  if (!isAllowed) {
                    window.alert(
                      "Domain is not whitelisted for the Print Pop-up Store, either whitelist this domain in Embed > Settings, or remove the embed code!"
                    );
                    console.log("Widget blocked: Domain not allowed");
                    return;
                  }
                }
              }

              // Check excluded URLs if any are configured
              if (data?.excluded_urls?.length > 0) {
                const currentPath = window.location.pathname;
                const isExcluded = data.excluded_urls.some((pattern) => {
                  try {
                    const regex = new RegExp(pattern);
                    return regex.test(currentPath);
                  } catch (e) {
                    console.error("Invalid URL pattern:", pattern);
                    return false;
                  }
                });

                if (isExcluded) {
                  console.log("Widget blocked: URL matches exclusion pattern");
                  return;
                }
              }

              // Check admin paths
              if (data?.matcher) {
                matcher = createPathMatcher(data.matcher);
              }

              if (!matcher(window.location.href)) {
                console.log("Widget blocked: Admin path detected");
                return;
              }

              // console.log("data:");
              // console.log(data);

              if (data?.design) {
                widgetContent.style.setProperty(
                  "--launcher-color",
                  data.design.launcher_color
                );
                widgetContent.style.setProperty(
                  "--launcher-text-color",
                  data.design.launcher_text_color
                );
                widgetContent.style.setProperty(
                  "--spacing-bottom",
                  data.design.launcher_bottom_spacing + "px"
                );
                widgetContent.style.setProperty(
                  "--spacing-side",
                  data.design.launcher_side_spacing + "px"
                );
                widgetContent.classList.add(
                  `print-solutions-alignment-${data.design.launcher_alignment}`
                );
              }
              widgetContent.classList.add("loaded");
              document.body.classList.add("print-solutions-loaded");
              state.isLoaded = true;

              // Add bubbles if they should be shown (won't show if minimized)
              if (shouldShowBubbles(data)) {
                renderBubblesOnLoad(data);
              }

              // Send loaded event
              sendMessageToIframe("widget_loaded", window.location.href);

              // Handle any pending actions after load
              if (state.pendingOpen) {
                window.printWidget.open();
                state.pendingOpen = false;
              }
              if (state.pendingNavigation) {
                handleNavigation(state.pendingNavigation);
                state.pendingNavigation = null;
              }
            }
          } else {
            // console.log("Received non-string message:", e.data);
          }
        } catch (error) {
          console.error("Error processing message::", error);
        }
      },
      false
    );
  }

  window.printWidget = {
    init: intPrintInterface,
    initialized: false,
    shadowRoot: null, // Add reference to shadowRoot
    open: () => {
      if (shadowRoot && !state.isOpen) {
        toggleOpen();
      } else if (!shadowRoot) {
        console.warn("Shadow DOM not initialized or not supported");
      }
    },
    close: () => {
      if (shadowRoot && state.isOpen) {
        toggleOpen();
      }
    },
    toggle: () => {
      if (shadowRoot) toggleOpen();
    },
  };

  // Function to render and animate bubbles on widget load
  function renderBubblesOnLoad(data) {
    // Only proceed if we should show bubbles and widget is not minimized
    if (!shouldShowBubbles(data) || shouldBeMinimized()) {
      return;
    }

    // Remove any existing bubbles first
    const existingBubbles = shadowRoot.getElementById("print-bubbles");
    if (existingBubbles) {
      existingBubbles.remove();
    }

    // Create and add bubbles
    const bubblesContainer = document.createElement("div");
    bubblesContainer.id = "print-bubbles";
    const bubbleContent = renderBubbles(data);

    // Only proceed if we have bubble content
    if (bubbleContent) {
      bubblesContainer.innerHTML = bubbleContent;
      $btn.appendChild(bubblesContainer);

      // Force a reflow to ensure animations work
      void bubblesContainer.offsetHeight;

      // Bubbles have been successfully rendered

      // Trigger animations with staggered delay
      setTimeout(() => {
        const bubbles = shadowRoot.querySelectorAll(".print-bubble");
        bubbles.forEach((bubble) => {
          const delay = parseInt(bubble.dataset.delay || 0) * 300;
          setTimeout(() => {
            bubble.classList.add("show");
          }, delay);
        });
      }, 100);
    }
  }

  // Function to render bubbles based on design configuration
  function renderBubbles(data) {
    const bubbles = data.design.bubbles;
    let bubblesHtml = renderCloseIcon();

    // Render intro bubble if enabled
    if (bubbles.intro?.enabled) {
      bubblesHtml += `
        <div class="print-bubble intro-bubble" data-delay="0">
          ${
            bubbles.intro.avatar?.enabled && bubbles.intro.avatar.url
              ? `
            <div class="bubble-avatar">
              <img src="${bubbles.intro.avatar.url}?w=100&q=80" alt="Avatar">
              ${bubbles.newMessageIndicator ? '<span class="attention-indicator"></span>' : ""}
            </div>
          `
              : ""
          }
          <div class="bubble-content">
            ${bubbles.intro.heading ? `<h4>${bubbles.intro.heading}</h4>` : ""}
            ${bubbles.intro.paragraph ? `<p>${bubbles.intro.paragraph}</p>` : ""}
          </div>
        </div>
      `;
    }

    // Render CTA buttons if any are enabled
    if (bubbles.ctas) {
      const activeCTAs = Object.entries(bubbles.ctas)
        .map(([key, cta]) => ({ key, ...cta }))
        .filter((cta) => cta.enabled)
        .sort((a, b) => a.order - b.order);

      activeCTAs.forEach((cta, index) => {
        // console.log(cta.key, index);
        // Determine the URL based on the CTA key
        let ctaUrl = cta.url; // default to the configured URL
        if (cta.key === "browse_catalogue") {
          ctaUrl = "#pp";
        } else if (cta.key === "contact") {
          ctaUrl = "#pp/message";
        }

        // Apply campaign-specific styling if available
        const buttonStyle =
          cta.color || cta.text_color
            ? `style="background-color: ${cta.color || "white"}; color: ${cta.text_color || "#333"};"`
            : "";

        bubblesHtml += `
          <div class="print-bubble cta-bubble" data-delay="${index + 1}">
            <a href="${ctaUrl}" class="cta-button" ${buttonStyle}>
              ${cta.text}
              ${cta.subtext ? `<div style="font-size: 12px; font-weight: normal; margin-top: 2px; opacity: 0.8;">${cta.subtext}</div>` : ""}
            </a>
          </div>
        `;
      });
    }

    return `
      <div class="print-bubbles-container animation-fadeUp">
        ${bubblesHtml}
      </div>
    `;
  }

  function toggleOpen() {
    const frameWidget = shadowRoot.getElementById("print-frame-widget");
    const existingBubbles = shadowRoot.getElementById("print-bubbles");
    const widgetContent = shadowRoot.getElementById("print-widget");

    if (state.isOpen) {
      // Close widget
      frameWidget.classList.remove("animate-open");
      frameWidget.classList.add("animate-close");
      document.body.classList.remove("print-solutions-open");
      widgetContent.classList.remove("print-solutions-open");

      // Wait for animation to complete before hiding
      setTimeout(() => {
        frameWidget.style.display = "none";
        frameWidget.classList.remove("animate-close");
      }, 300); // Match the transition duration

      sendMessageToIframe("widget_closed", window.location.href);
    } else {
      // Open widget
      document.body.classList.add("print-solutions-open");
      widgetContent.classList.add("print-solutions-open");
      frameWidget.style.display = "block";

      // Trigger reflow to ensure the animation works
      void frameWidget.offsetWidth;

      // Add animation class
      frameWidget.classList.add("animate-open");

      // Remove existing bubbles when opening widget
      if (existingBubbles) {
        existingBubbles.remove();
      }

      sendMessageToIframe("widget_opened", window.location.href);
    }
    state.isOpen = !state.isOpen;
  }

  // Expose toggleOpen to window for external access
  window.printWidgetToggleOpen = function () {
    if (shadowRoot) {
      toggleOpen();
    }
  };

  // Expose minimize function to window for direct access from inline handlers
  window.printWidgetMinimize = function () {
    if (shadowRoot) {
      document.body.classList.add("popup-store-launcher-minimized");
      const widgetContent = shadowRoot.getElementById("print-widget");
      widgetContent.classList.add("popup-store-launcher-minimized");
      removeBubbles();

      // Store minimization timestamp
      try {
        const storageKey = "print_widget_minimized_timestamp";
        const currentTime = new Date().getTime();
        localStorage.setItem(storageKey, currentTime.toString());
      } catch (error) {
        console.error("Error setting minimization timestamp:", error);
      }
    }
  };

  // Add this function to handle sending messages to the iframe
  function sendMessageToIframe(type, payload) {
    const frameWidget = shadowRoot.getElementById("print-frame-widget");
    if (frameWidget) {
      // Wait a bit to ensure iframe is ready
      setTimeout(() => {
        const iframeSrc = frameWidget.src;
        const iframeOrigin = new URL(iframeSrc).origin;

        frameWidget.contentWindow.postMessage(
          {
            type,
            payload,
          },
          iframeOrigin
        );
      }, 500);
    }
  }

  // Then modify handleNavigation to use this function
  function handleNavigation(path) {
    sendMessageToIframe("navigate", path);
  }

  function handleHashChange() {
    const hash = window.location.hash;
    const matchingHash = openHashes.find((h) => hash.startsWith(h));

    if (matchingHash) {
      // Remove hash without causing page jump
      history.pushState(
        {},
        "",
        window.location.pathname + window.location.search
      );

      // Extract the path after the hash, accounting for both #print-popup and #pp
      const path = hash.substring(matchingHash.length);

      if (state.isLoaded && shadowRoot) {
        window.printWidget.open();
      } else {
        state.pendingOpen = true;
      }

      // If there's a path, handle navigation
      if (path && path.length > 0) {
        // Handle navigation with path
        if (state.isLoaded) {
          handleNavigation(path.substring(1)); // Keep the substring(1) to remove the leading /
        } else {
          state.pendingNavigation = path.substring(1); // Keep the substring(1) to remove the leading /
        }
      }
    }
  }

  function initPrintWidget() {
    if (!window.printWidget.initialized) {
      window.printWidget.initialized = true;
      window.printWidget.init();

      // Add hash change listener
      window.addEventListener("hashchange", handleHashChange);

      // Check hash on initial load - Check for any matching hash
      const currentHash = window.location.hash;
      if (openHashes.some((hash) => currentHash.startsWith(hash))) {
        handleHashChange();
      }
    }
  }

  // Use DOMContentLoaded to ensure the DOM is fully loaded before initializing
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initPrintWidget);
  } else {
    // DOMContentLoaded has already fired
    initPrintWidget();
  }

  // Add this new function
  function renderCloseIcon() {
    return `
      <div id="trigger-close-icon">
        <div class="trigger-close-icon-inner" onclick="window.printWidgetMinimize()">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1.00005L11 11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <path d="M1 11L11 0.999999" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>          
        </div>
      </div>
    `;
  }

  function removeBubbles() {
    const bubbles = shadowRoot.querySelectorAll(".print-bubble");
    if (!bubbles.length) return;

    // Animate out bubbles in reverse order
    bubbles.forEach((bubble, index) => {
      const delay = (bubbles.length - index - 1) * 100; // Stagger removal from last to first
      setTimeout(() => {
        bubble.style.opacity = "0";
        bubble.style.transform = "translateY(20px)";
      }, delay);
    });

    // Remove the entire bubbles container after all animations complete
    const totalDelay = bubbles.length * 100 + 500; // Add extra time for the animation to complete
    setTimeout(() => {
      const existingBubbles = shadowRoot.getElementById("print-bubbles");
      if (existingBubbles) {
        existingBubbles.remove();
      }
    }, totalDelay);
  }

  // Global state for attention bars
  let attentionBarState = {
    activeBars: [],
    scrollListeners: [],
    displayTimers: [],
  };

  // Helper function to determine URL based on campaign objective
  function getCampaignUrl(campaign) {
    const objective = campaign.objective;
    const settings = campaign.attention_bar_settings;

    switch (objective) {
      case "promote_products":
        return `#pp/campaigns/${campaign.slug}`;
      case "get_contacts":
        return "#pp/message";
      case "announcement":
        return null; // No link for announcements
      case "target_url":
        return settings.target_url || null;
      default:
        return `#pp/campaigns/${campaign.slug}`;
    }
  }

  // Helper function to check cooldown period
  function isInCooldownPeriod(campaignId, cooldownPeriod) {
    if (!cooldownPeriod || cooldownPeriod === "display_every_page_load") {
      return false;
    }

    try {
      const cooldownKey = `attention_bar_dismissed_${campaignId}`;
      const dismissedTime = localStorage.getItem(cooldownKey);

      if (!dismissedTime) {
        return false;
      }

      const now = new Date().getTime();
      const timeDiff = now - parseInt(dismissedTime);

      // Parse cooldown period
      let cooldownMs = 0;
      if (cooldownPeriod.includes("day")) {
        const days = parseInt(cooldownPeriod);
        cooldownMs = days * 24 * 60 * 60 * 1000;
      } else if (cooldownPeriod === "never_display_again") {
        return true; // Always in cooldown
      }

      return timeDiff < cooldownMs;
    } catch (error) {
      console.error("Error checking cooldown period:", error);
      return false;
    }
  }

  // Helper function to set cooldown period
  function setCooldownPeriod(campaignId) {
    try {
      const cooldownKey = `attention_bar_dismissed_${campaignId}`;
      const currentTime = new Date().getTime();
      localStorage.setItem(cooldownKey, currentTime.toString());
    } catch (error) {
      console.error("Error setting cooldown period:", error);
    }
  }

  // Function to calculate sticky top offset for attention bars
  function calculateStickyTopOffset(currentBarIndex) {
    let totalHeight = 0;

    // Sum heights of sticky bars that come before this bar
    for (let i = 0; i < currentBarIndex; i++) {
      const bar = attentionBarState.activeBars[i];
      if (bar && bar.element && bar.settings.remain_on_scroll) {
        totalHeight += bar.element.offsetHeight || bar.settings.size || 55;
      }
    }

    return totalHeight;
  }

  // Function to update sticky bar positions
  function updateStickyBarPositions() {
    attentionBarState.activeBars.forEach((bar, index) => {
      if (bar.element && bar.settings.remain_on_scroll) {
        const topOffset = calculateStickyTopOffset(index);
        bar.element.style.top = `${topOffset}px`;
      }
    });
  }

  // Function to update body padding for pushes_page_down functionality
  function updateBodyPadding() {
    let totalPaddingHeight = 0;

    attentionBarState.activeBars.forEach((bar) => {
      // Only count bars that:
      // 1. Have pushes_page_down enabled
      // 2. Are currently shown (have 'show' class - meaning they've been triggered)
      if (
        bar.element &&
        bar.settings.pushes_page_down &&
        bar.element.classList.contains("show")
      ) {
        totalPaddingHeight +=
          bar.element.offsetHeight || bar.settings.size || 55;
      }
    });

    // Apply padding to body
    if (totalPaddingHeight > 0) {
      document.body.style.paddingTop = `${totalPaddingHeight}px`;
    } else {
      document.body.style.paddingTop = "";
    }
  }

  // Function to remove attention bar
  function removeAttentionBar(campaignId) {
    const barIndex = attentionBarState.activeBars.findIndex(
      (bar) => bar.campaignId === campaignId
    );
    if (barIndex === -1) return;

    const barData = attentionBarState.activeBars[barIndex];
    const element = barData.element;

    if (element) {
      // Animate out if enabled
      if (barData.settings.animate_entry_exit) {
        element.classList.add("animate-exit");
        setTimeout(() => {
          if (element.parentNode) {
            element.parentNode.removeChild(element);
          }
        }, 300);
      } else {
        if (element.parentNode) {
          element.parentNode.removeChild(element);
        }
      }
    }

    // Remove from active bars
    attentionBarState.activeBars.splice(barIndex, 1);

    // Update positions of remaining bars
    setTimeout(
      () => {
        updateStickyBarPositions();
        updateBodyPadding();
      },
      barData.settings.animate_entry_exit ? 300 : 0
    );
  }

  // Function to update positions of all attention bars (deprecated - using sticky positioning now)
  function updateAttentionBarPositions() {
    // This function is kept for backward compatibility but functionality moved to updateStickyBarPositions
    updateStickyBarPositions();
  }

  // Function to create attention bar HTML
  function createAttentionBarElement(campaign) {
    const settings = campaign.attention_bar_settings;
    const campaignUrl = getCampaignUrl(campaign);

    const attentionBar = document.createElement("div");
    attentionBar.className = "campaign-attention-bar";

    // Add positioning class based on remain_on_scroll setting
    if (!settings.remain_on_scroll) {
      attentionBar.classList.add("non-sticky");
    }

    if (settings.animate_entry_exit) {
      attentionBar.classList.add("animate-entry");
    }
    attentionBar.setAttribute("data-campaign-id", campaign.id);

    // Set CSS custom properties for dynamic styling
    attentionBar.style.setProperty(
      "--attention-bar-bg",
      settings.background_color || "#000000"
    );
    attentionBar.style.setProperty(
      "--attention-bar-text",
      settings.text_color || "#ffffff"
    );
    attentionBar.style.setProperty(
      "--close-color",
      settings.close_button_color || "#ff0000"
    );
    attentionBar.style.height = `${settings.size || 55}px`;

    // Set initial top position (will be updated by updateStickyBarPositions)
    if (settings.remain_on_scroll) {
      attentionBar.style.top = "0px"; // Will be calculated properly when added to DOM
    }

    // Create content container
    const content = document.createElement("div");
    content.className = "attention-bar-content";

    // Create text content
    const textContent = document.createElement("div");
    textContent.className = "attention-bar-text";

    // Add title
    if (settings.title) {
      const title = document.createElement("div");
      title.className = `attention-bar-title${settings.subtitle ? " has-subtitle" : ""}`;
      title.textContent = settings.title;
      textContent.appendChild(title);
    }

    // Add subtitle
    if (settings.subtitle) {
      const subtitle = document.createElement("div");
      subtitle.className = "attention-bar-subtitle";
      subtitle.textContent = settings.subtitle;
      textContent.appendChild(subtitle);
    }

    content.appendChild(textContent);

    // Add CTA button if configured and URL exists
    if (settings.cta_button && campaignUrl) {
      const ctaButton = document.createElement("button");
      ctaButton.className = `attention-bar-cta${settings.wiggle_button ? " wiggle" : ""}`;

      // Set CTA button CSS custom properties
      ctaButton.style.setProperty(
        "--cta-bg",
        settings.cta_button.background_color || "#1284ff"
      );
      ctaButton.style.setProperty(
        "--cta-text",
        settings.cta_button.text_color || "#ffffff"
      );
      ctaButton.style.setProperty(
        "--cta-border-width",
        `${settings.cta_button.border_width || 0}px`
      );
      ctaButton.style.setProperty(
        "--cta-border-color",
        settings.cta_button.border_color || "transparent"
      );
      ctaButton.style.setProperty(
        "--cta-border-radius",
        `${settings.cta_button.border_radius || 2}px`
      );
      ctaButton.style.setProperty(
        "--cta-height",
        `${settings.cta_button.height || 41}px`
      );

      ctaButton.textContent = settings.cta_button.title || "Click Here";

      // Add click handler
      ctaButton.addEventListener("click", () => {
        window.location.hash = campaignUrl;
      });

      content.appendChild(ctaButton);
    }

    // Add close button
    const closeButton = document.createElement("button");
    closeButton.className = "attention-bar-close";
    closeButton.innerHTML = "×";

    closeButton.addEventListener("click", () => {
      removeAttentionBar(campaign.id);
      setCooldownPeriod(campaign.id);
    });

    content.appendChild(closeButton);
    attentionBar.appendChild(content);

    return attentionBar;
  }

  // Function to display attention bar with animation
  function displayAttentionBar(campaign) {
    const element = createAttentionBarElement(campaign);
    const settings = campaign.attention_bar_settings;

    // Add to attention bar shadow DOM
    const attentionBarsContainer = attentionBarShadowRoot.querySelector(
      ".attention-bars-container"
    );
    attentionBarsContainer.appendChild(element);

    // Store in active bars
    attentionBarState.activeBars.push({
      campaignId: campaign.id,
      element: element,
      settings: settings,
    });

    // Update sticky bar positions
    updateStickyBarPositions();

    // Trigger animation
    setTimeout(() => {
      element.classList.add("show");
      // Update body padding after bar becomes visible
      updateBodyPadding();
    }, 50);
  }

  // Throttle function for performance
  function throttle(func, limit) {
    let inThrottle;
    return function () {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }

  // Function to handle scroll-based triggers
  function setupScrollTriggers() {
    const throttledScrollHandler = throttle(() => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollPercent = (scrollTop / (documentHeight - windowHeight)) * 100;

      // Check for scroll-based triggers
      attentionBarState.activeBars.forEach((bar) => {
        const trigger = bar.settings.display_trigger;
        let shouldTrigger = false;

        if (trigger === "after_scrolling_little" && scrollTop >= 100) {
          shouldTrigger = true;
        } else if (
          trigger === "after_scrolling_middle" &&
          scrollPercent >= 50
        ) {
          shouldTrigger = true;
        } else if (
          trigger === "after_scrolling_bottom" &&
          scrollPercent >= 90
        ) {
          shouldTrigger = true;
        }

        if (
          shouldTrigger &&
          bar.element &&
          !bar.element.classList.contains("show")
        ) {
          bar.element.classList.add("show");
          // Update body padding when bar becomes visible
          updateBodyPadding();
        }
      });
    }, 100); // Throttle to 10fps

    window.addEventListener("scroll", throttledScrollHandler);
    attentionBarState.scrollListeners.push(throttledScrollHandler);
  }

  // Function to handle exit intent
  function setupExitIntentTrigger() {
    let hasTriggered = false;

    const exitIntentHandler = (e) => {
      if (hasTriggered) return;

      // Check if mouse is leaving the top of the viewport
      if (e.clientY <= 0) {
        hasTriggered = true;

        // Trigger any bars waiting for exit intent
        attentionBarState.activeBars.forEach((bar) => {
          if (
            bar.settings.display_trigger === "exit_intent" &&
            bar.element &&
            !bar.element.classList.contains("show")
          ) {
            bar.element.classList.add("show");
          }
        });

        // Update body padding after any bars become visible
        updateBodyPadding();
      }
    };

    document.addEventListener("mouseleave", exitIntentHandler);
  }

  // Function to render attention bars for active campaigns
  function renderAttentionBars(campaigns) {
    if (!Array.isArray(campaigns)) {
      return;
    }

    // Sort campaigns by priority (higher priority first), then by creation date
    const sortedCampaigns = campaigns
      .filter((campaign) => campaign.attention_bar_settings?.enabled)
      .sort((a, b) => {
        const priorityA = a.attention_bar_settings?.priority || 5;
        const priorityB = b.attention_bar_settings?.priority || 5;

        // Higher priority first
        if (priorityA !== priorityB) {
          return priorityB - priorityA;
        }

        // If same priority, sort by creation date (newer first)
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });

    sortedCampaigns.forEach((campaign) => {
      const settings = campaign.attention_bar_settings;

      // Check cooldown period
      if (isInCooldownPeriod(campaign.id, settings.cooldown_period)) {
        return;
      }

      // Handle display triggers
      const trigger = settings.display_trigger || "immediately";

      if (trigger === "immediately") {
        displayAttentionBar(campaign);
      } else if (trigger.includes("second_delay")) {
        const seconds = parseInt(trigger);
        const timer = setTimeout(() => {
          displayAttentionBar(campaign);
        }, seconds * 1000);
        attentionBarState.displayTimers.push(timer);
      } else if (trigger.includes("minutes_delay")) {
        const minutes = parseInt(trigger);
        const timer = setTimeout(
          () => {
            displayAttentionBar(campaign);
          },
          minutes * 60 * 1000
        );
        attentionBarState.displayTimers.push(timer);
      } else if (
        trigger.startsWith("after_scrolling") ||
        trigger === "exit_intent"
      ) {
        // Create hidden bar that will be shown on trigger
        const element = createAttentionBarElement(campaign);
        // Don't add show class yet - will be added on trigger
        const attentionBarsContainer = attentionBarShadowRoot.querySelector(
          ".attention-bars-container"
        );
        attentionBarsContainer.appendChild(element);

        attentionBarState.activeBars.push({
          campaignId: campaign.id,
          element: element,
          settings: settings,
        });

        updateStickyBarPositions();
      }
    });

    // Set up scroll and exit intent listeners if needed
    setupScrollTriggers();
    setupExitIntentTrigger();
  }
}
